import { useAppSelector } from '@app/store/hooks'
import { useEffect, useState, useRef } from 'react'

/**
 * Hook that dynamically loads static content for the current language while preserving previous content
 * during language changes to prevent loading screens after initial load
 * @returns The static content, loading state, and boolean indicating if content has ever been loaded
 */
const useStaticContent = () => {
  const { language } = useAppSelector(state => state.settings)
  const [content, setContent] = useState<Record<string, string>>({})
  const [loaded, setLoaded] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const previousContentRef = useRef<Record<string, string>>({})

  /**
   * Effect to dynamically loads the content each time the language changes
   */
  useEffect(() => {
    // For subsequent loads, keep loaded=true to avoid showing loading screen
    // Only set loaded=false on the very first load
    if (isInitialLoad) {
      setLoaded(false)
    }

    import(`@app/lang/common/${language}/defaults.ts`)
      .then(module => {
        // Store previous content before updating
        if (Object.keys(content).length > 0) {
          previousContentRef.current = content
        }

        setContent(module.default)
        setLoaded(true)

        // After first successful load, we're no longer in initial load state
        if (isInitialLoad) {
          setIsInitialLoad(false)
        }
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.warn(`Could not load language file for ${language}`)

        // On error, only set loaded=false if this is the initial load
        // Otherwise keep previous content and loaded state
        if (isInitialLoad) {
          setLoaded(false)
        } else {
          // Keep previous content on error for non-initial loads
          // eslint-disable-next-line no-console
          console.warn('Using previous language content due to load error')
        }
      })
  }, [language, content, isInitialLoad])

  return {
    content,
    loaded,
    isInitialLoad,
    previousContent: previousContentRef.current,
  }
}

export default useStaticContent
